import React, { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useEntitySettings } from '@context/EntityContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Heading4 } from '@ui/Typography'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getTickmillCompanyByHostname, getTickmillSupportEmail } from '@utils/companyName.utils'

import { Seo } from '@global/Seo/Seo'
import { AppMarketLink } from '@global/appMarketLink/mobile/AppMarketLink'

import { usePostLoginNavigation } from '@hooks/usePostLoginRedirectPath'
import { useScrollToTop } from '@hooks/useScrollToTop'
import { safeLocalStorage } from '@hooks/useStorage'

import { getAmplitudeIdentifyProperties, identifyAmplitudeUser } from '@analytics/amplitude'

import { TwoFactorAuthEvents, TwoFactorErrors } from '../TwoFactorAuth/useTwoFactorAuth'
import { LoginForm, LoginFormValues } from './LoginForm'
import { useSignInAuth } from './useSignInAuth'

import styles from './LoginPage.module.scss'

export interface LoginPageProps {
  skipLogout?: boolean
}

export const LoginPage: React.FunctionComponent<LoginPageProps> & LoginPageProps = (props) => {
  useScrollToTop()

  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [entity, setEntity] = useEntitySettings()
  const actualEntity = entity.entity
  useEffect(() => {
    // setEntity to default when visit Login Page.
    if (!props.skipLogout) {
      setEntity({ entity: getTickmillCompanyByHostname() })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { handleSignInAuth } = useSignInAuth()
  const { navigateAfterLogin } = usePostLoginNavigation()

  const onLogin = async (values: LoginFormValues) => {
    try {
      if (props.skipLogout) {
        return undefined
      }

      const loginResponse = await apiClient.login(values)
      const hasTwoFactor = (loginResponse?.twoFactor?.length ?? 0) > 0

      if (hasTwoFactor) {
        safeLocalStorage.setItem('2fa.email', loginResponse.email)
        navigate(`/login/2fa${window.location.search}`, {
          state: {
            twoFactorAuthData: {
              ...loginResponse,
              password: values.password,
              twoFactorAuthEvent: TwoFactorAuthEvents.LogIn,
            },
          },
        })
        return
      } else {
        const signInResult = await handleSignInAuth(loginResponse)
        if (!signInResult.shouldContinueNavigation || !signInResult.auth) {
          return
        }

        identifyAmplitudeUser(
          signInResult.auth.id,
          getAmplitudeIdentifyProperties(signInResult.auth)
        )
        await navigateAfterLogin()
      }
    } catch (error: unknown) {
      if (error instanceof ResponseError) {
        const { response } = error.response
        if (response?.data.code === 'invalid_user_status_vulnerable_client') {
          const supportEmail =
            response?.data?.properties?.companyAndSupport?.SupportDto?.SupportEmail ??
            getTickmillSupportEmail(getTickmillCompanyByHostname(true))

          addSnackbar.error({
            message: '',
            content: (
              <Trans
                i18nKey='Sign up.From the information you have provided, we do not think this product is appropriate for you at this time. If you would like to discuss further, please contact {{email}}.'
                values={{ email: supportEmail }}
                components={{
                  a: <a href={`mailto:${supportEmail}`} target='_blank' rel='noreferrer' />,
                }}
              />
            ),
            closable: true,
            noTimeout: true,
          })
        } else if (
          response?.data.code === TwoFactorErrors.TooManyAttempts ||
          response?.data.code === TwoFactorErrors.TooManySends
        ) {
          navigate('/login/2fa/expired', {
            state: {
              twoFactorAuthData: {
                twoFactorAuthEvent: TwoFactorAuthEvents.LogIn,
                email: values.email,
              },
            },
          })
          const errorTimer = error.response.response?.data.properties?.cooldownInMinutes || '30'

          addSnackbar.error({
            message: t('errors.Too many resend attempts', { minutes: errorTimer }),
          })
        } else if (response?.data.code === 'invalid_credentials') {
          addSnackbar.error({ message: t('errors.Invalid credentials') })
        } else {
          return
        }
      }
      console.error(error)
      throw error
    }
  }

  return (
    <div className={styles.topWrapper}>
      <Seo title={t('meta.login.title')} description={t('meta.login.description')} path='login' />
      <div className={styles.wrapper}>
        <div className={styles.inner}>
          <Heading4 medium>{t('Log in')}</Heading4>
          <LoginForm entity={actualEntity} onSubmit={onLogin} />
          <AppMarketLink />
        </div>
      </div>
    </div>
  )
}
